/**
 * Enrollment category config
 *
 * KPIs: class, currentEnrollment, sanctionedSection, expectedEnrollment
 *
 * Special behaviour:
 *  - Grouped bar chart (current vs expected enrollment) for class/currentEnrollment/expectedEnrollment
 *  - Aggregated bar chart for sanctionedSection (sum per class)
 */
import type { CategoryConfig, ChartDescriptor } from './CategoryConfig';

export const enrollmentConfig: CategoryConfig = {
  categoryKey: 'enrollmentProfile',
  label: 'Enrollment',
  priority: 3,

  columns: {
    defaultVisibleColumns: [
      'schoolName', 'stateName', 'roName', 'class', 'currentEnrollment',
      'expectedEnrollment', 'sanctionedSection',
    ],
  },

  chart: (kpi): ChartDescriptor | null => {
    const lower = kpi.toLowerCase();

    // ── Sanctioned Section: sum per-class ──
    if (lower === 'sanctionedsection' || lower === 'sanctioned section') {
      return {
        type: 'bar',
        title: (_, filtered) =>
          `Total Sanctioned Sections by Class (All Schools)${filtered ? ' (Filtered)' : ''}`,
        xAxisLabel: 'Class',
        yAxisLabel: 'Total Sanctioned Sections',
        buildData: (ctx) => {
          const map: Record<number, number> = {};
          ctx.kpiDataItems.forEach(item => {
            if (!Array.isArray(item.kvs_details)) return;
            ctx.applyFilters(item.kvs_details, ctx.globalFilters).forEach((s: any) => {
              const cls = Number(s.class || s.classID || 0);
              const sec = Number(s.sanctionedSection || s.sanctioned_section || 0);
              if (cls > 0) map[cls] = (map[cls] || 0) + sec;
            });
          });
          return Object.keys(map)
            .map(Number)
            .sort((a, b) => a - b)
            .map(c => ({ sortKey: c, name: `Class ${c}`, value: map[c] }));
        },
      };
    }

    // ── Class / Current / Expected: grouped bar ──
    if (
      lower === 'class' ||
      lower === 'currentenrollment' ||
      lower === 'current enrollment' ||
      lower === 'expectedenrollment' ||
      lower === 'expected enrollment'
    ) {
      return {
        type: 'groupedBar',
        title: (_, filtered) => {
          const prefix = lower === 'class' ? 'Class-wise' : 'Region-wise';
          return `${prefix} Enrollment Comparison${filtered ? ' (Filtered)' : ''}`;
        },
        xAxisLabel: lower === 'class' ? 'Class' : 'State/Region',
        yAxisLabel: 'Number of Students',
        buildData: (ctx) =>
          ctx.kpiDataItems
            .map(item => {
              const cls =
                item.class || item.Class || item.roName || item.stateName ||
                item.districtName || 'Item';
              let cur = 0;
              let exp = 0;
              if (Array.isArray(item.kvs_details)) {
                ctx.applyFilters(item.kvs_details, ctx.globalFilters).forEach((s: any) => {
                  cur += Number(s.currentEnrollment || s.current_enrollment || s.CurrentEnrollment || 0);
                  exp += Number(s.expectedEnrollment || s.expected_enrollment || s.ExpectedEnrollment || 0);
                });
              }
              return { class: cls, currentEnrollment: cur, expectedEnrollment: exp };
            })
            .filter(d => d.currentEnrollment > 0 || d.expectedEnrollment > 0),
      };
    }

    return null; // fall through to default chart
  },
};
